/* =========================================================================
 * WHO IS THE CUSTOMER — the champion, tied to a real HubSpot company.
 *
 * Brian: "once the command center finds the champions, we need to link hubspot
 * and find the actual customer and if the customer doesn't exist, we must build
 * the customer and route the pursuit and outreach game-plan."
 *
 * This is the FIRST half of that, and it is honest about being the first half.
 * `POST /api/champion-company` has existed and worked the whole time with
 * nothing calling it — a crowned record could name a school and give a rep no
 * way to reach the account. That is the gap this closes.
 *
 * ⚠️ IT LOOKS BEFORE IT TOUCHES, AND THE SPLIT IS THE POINT.
 *
 * Opening this panel runs a LOOKUP that writes nothing. The tie only happens
 * when a person presses a named company. A wrong tie points a rep at the wrong
 * customer — two schools with the same name in different states is the single
 * most common way this goes wrong — and an untied record is a far better
 * outcome than a confidently wrong one.
 *
 * So the four answers the server can give are rendered as four different
 * things, never flattened into "no match":
 *
 *   exact / strong   one company, and it says WHY it is confident. One click.
 *   ambiguous        several plausible. The server refuses to choose and so
 *                    does this — every candidate is listed with its score.
 *   none             a TAM gap, said plainly. Nobody has this customer yet,
 *                    which is a thing to act on, not an error to hide.
 *   searchFailed     we could not look. NEVER shown as "none" — see below.
 *
 * "We could not look" and "there is nothing there" are opposite outcomes and
 * the backend is careful to distinguish them; throwing that away in the UI
 * would file a real customer under "TAM we do not have" and nobody would
 * check again.
 * ========================================================================= */

import { useState } from 'react'
import { api } from '../../../lib/api.js'
import { useAccess } from '../../../lib/access.jsx'
import { ACTIONS } from '../../../lib/rbac.js'
import { championKind, PERSON_CUSTOMERS } from './championKind.js'
import styles from './pursuit.module.css'

const PORTAL = '51263144'
const companyUrl = (id) => `https://app.hubspot.com/contacts/${PORTAL}/record/0-2/${encodeURIComponent(id)}`
/* THE DOORS INTO HUBSPOT. Brian: "it doesnt give me an option to go create one."
 *
 * There is no create route on the backend and this build does not invent one:
 * WHICH object to create is the open question (see PERSON_CUSTOMERS), and a
 * create button that guesses would put a vaulter in the company table.
 *
 * What was wrong was having no door at all. These are HubSpot's own object
 * lists, filtered to the name — the rep lands on the search already run, with
 * HubSpot's Create button right there. Deliberately NOT described as
 * pre-filling the new record: HubSpot has no reliable URL that does that, and
 * promising it would be the same class of lie as "not built yet" was. */
const searchUrl = (obj, q) =>
  `https://app.hubspot.com/contacts/${PORTAL}/objects/${obj}/views/all/list?query=${encodeURIComponent(q)}`
const COMPANY_OBJ = '0-2'
const CONTACT_OBJ = '0-1'

export default function CompanyTie({ record, onTied }) {
  const { can, why } = useAccess()
  const [state, setState] = useState('idle') // idle | looking | looked | tying | tied | error
  const [res, setRes] = useState(null)
  const [msg, setMsg] = useState('')
  const [tiedTo, setTiedTo] = useState(null)

  const id = String(record?.id || '')
  const champion = String(record?.champion || record?.winningSchool || '')
  /* ⚠️ A PERSON IS NOT A MISSING COMPANY. See championKind.js. */
  const kind = championKind(champion, { sport: record?.sport })

  /* No champion, nothing to resolve. Said rather than rendered as an empty
   * panel, because "there is no customer" and "we have not looked" are
   * different and only one of them is this record's fault. */
  if (!id || record?.sourceType) return null

  const mayTie = can(ACTIONS.CC_CHAMPION_UPDATE, record)

  const look = async () => {
    setState('looking'); setMsg('')
    try {
      const r = await api.championCompanyLookup(id, champion)
      setRes(r)
      setState('looked')
    } catch (err) {
      setState('error')
      setMsg(err?.body?.reason || err?.body?.error || err?.message || 'The lookup failed.')
    }
  }

  const tie = async (company) => {
    setState('tying'); setMsg('')
    try {
      await api.championCompanyAttach(id, company.id, `tied from the Command Center by a human — ${res?.match || 'reviewed'}`)
      setTiedTo(company)
      setState('tied')
      onTied && onTied(id, company)
    } catch (err) {
      setState('error')
      setMsg(err?.body?.detail ? JSON.stringify(err.body.detail).slice(0, 180) : (err?.message || 'Could not attach the company.'))
    }
  }

  if (!champion) {
    return (
      <div className={styles.tieNote}>
        No champion on this record yet, so there is no customer to look up. Find or
        enter the champion first.
      </div>
    )
  }

  return (
    <div className={styles.tie}>
      <div className={styles.tieHead}>
        <b>The customer</b>
        <span className={styles.tieSub}>
          {kind === 'person'
            ? `“${champion}” is an individual — the customer is the club, the athlete or a parent`
            : `Match “${champion}” to a HubSpot company`}
        </span>
      </div>

      {/* ⚠️ SAID BEFORE THE SEARCH RUNS, NOT AFTER IT COMES BACK EMPTY.
          An individual champion will never match a company, so letting the rep
          press the button and read "no company resembles Danica Rinard" teaches
          them the system is broken. The company search is still offered — the
          club is very often the real account and its name may resemble hers. */}
      {kind === 'person' && (
        <div className={styles.tieNote}>
          A company search will not find a person. SR&rsquo;s customer here is one
          of three, and the record cannot tell which — you can:
          <ul className={styles.tieList}>
            {PERSON_CUSTOMERS.map(([k, label, whyIt]) => (
              <li key={k}><b>{label}</b> — {whyIt}</li>
            ))}
          </ul>
        </div>
      )}

      {state === 'idle' && (
        <div className={styles.tieActions}>
          <button type="button" className={styles.tieBtn} onClick={look}>
            {kind === 'person' ? 'Search companies for their club anyway' : 'Find the customer in HubSpot'}
          </button>
          {kind !== 'organisation' && (
            <a href={searchUrl(CONTACT_OBJ, champion)} target="_blank" rel="noopener noreferrer" className={styles.tieLink}>
              Search HubSpot contacts for “{champion}” ↗
            </a>
          )}
        </div>
      )}

      {state === 'looking' && <div className={styles.tieNote}>Looking…</div>}
      {state === 'tying' && <div className={styles.tieNote}>Attaching…</div>}

      {state === 'error' && <div className={styles.tieBad}>{msg}</div>}

      {state === 'tied' && tiedTo && (
        <div className={styles.tieGood}>
          ✓ Tied to <b>{tiedTo.name}</b>.{' '}
          <a href={companyUrl(tiedTo.id)} target="_blank" rel="noopener noreferrer">Open in HubSpot ↗</a>
        </div>
      )}

      {state === 'looked' && res && (
        <div className={styles.tieBody}>
          {/* ⚠️ A FAILED SEARCH IS NOT A NO-MATCH. Kept first and kept loud,
              because reporting it as "none" is how a real customer gets filed
              under TAM-we-do-not-have and never looked at again. */}
          {res.searchFailed ? (
            <div className={styles.tieBad}>
              <b>We could not look.</b> {res.reason} — this is NOT “no customer”. Try again.
            </div>
          ) : res.match === 'none' ? (
            <div className={styles.tieGap}>
              <b>
                {kind === 'person'
                  ? `No company matches “${champion}” — expected, they are a person.`
                  : `No company in HubSpot resembles “${champion}”.`}
              </b>
              <div>{res.reason}</div>
              {kind === 'person'
                ? <div>Look for the club they compete for, or add them as a contact.</div>
                : <div className={styles.tieGapNote}>
                    That is a TAM gap: a champion nobody holds an account for. Worth creating.
                  </div>}
              {/* ⚠️ THE DOOR. This used to read "creating the company from here
                  is not built yet", which left the rep at a dead end on the one
                  screen that had found the problem. Creating still happens in
                  HubSpot — but the search is already run when they land, and
                  coming back here ties it. */}
              <div className={styles.tieActions}>
                <a href={searchUrl(COMPANY_OBJ, champion)} target="_blank" rel="noopener noreferrer" className={styles.tieLink}>
                  Create the company in HubSpot ↗
                </a>
                <a href={searchUrl(CONTACT_OBJ, champion)} target="_blank" rel="noopener noreferrer" className={styles.tieLink}>
                  Create a contact instead ↗
                </a>
                <button type="button" className={styles.tieBtnSm} onClick={look}>
                  Made it — look again
                </button>
              </div>
            </div>
          ) : res.company ? (
            <>
              <div className={styles.tieMatch}>
                <span className={styles.tieBadge} data-match={res.match}>{res.match}</span>
                <b>{res.company.name}</b>
                {res.company.state ? <span className={styles.tieSub}> · {res.company.state}</span> : null}
                {res.company.city ? <span className={styles.tieSub}> · {res.company.city}</span> : null}
              </div>
              <div className={styles.tieWhy}>{res.reason}</div>
              <div className={styles.tieActions}>
                <a href={companyUrl(res.company.id)} target="_blank" rel="noopener noreferrer" className={styles.tieLink}>
                  Open in HubSpot ↗
                </a>
                {mayTie ? (
                  <button type="button" className={styles.tieBtn} onClick={() => tie(res.company)}>
                    Tie this company to the championship
                  </button>
                ) : (
                  <span className={styles.tieSub}>{why(ACTIONS.CC_CHAMPION_UPDATE, record) || 'You cannot change this record.'}</span>
                )}
              </div>
            </>
          ) : (
            <>
              {/* AMBIGUOUS. The server refused to pick and so does this. Every
                  candidate is shown with its score, because the rep knows
                  things the score does not. */}
              <div className={styles.tieWhy}><b>Several companies match closely.</b> {res.reason}</div>
              <ul className={styles.tieList}>
                {(res.candidates || []).map((c) => (
                  <li key={c.id}>
                    <span className={styles.tieScore}>{c.score}</span>
                    <b>{c.name}</b>
                    {c.state ? <span className={styles.tieSub}> · {c.state}</span> : null}
                    {c.orgType ? <span className={styles.tieSub}> · {c.orgType}</span> : null}
                    <a href={companyUrl(c.id)} target="_blank" rel="noopener noreferrer" className={styles.tieLink}>open ↗</a>
                    {mayTie && (
                      <button type="button" className={styles.tieBtnSm} onClick={() => tie(c)}>This one</button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  )
}
